import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  DatePickerInput,
  TimePickerInput,
  IcPick as icPick,
  IcTarget as icTarget,
} from '@/shared/ui';
import useEventRangePickers from './useEventRangePickers';

export type EventRangePickersProps = {
  startDate?: any;
  startTime?: any;
  endDate?: any;
  endTime?: any;
  error: any;
  onChangePicker: (name: string, time: any, timeString?: string | string[]) => void;
};

/**
 * Renders the paired start and end date and time pickers shared by the beat tab's event modals. Both rows use read-only inputs showing the per-field error text from props, and each carries jump-to and pick-on-ECG-viewer buttons that call the hook handlers with a flag naming which end of the range they target.
 *
 * @param props - Picker props with the start and end values plus the per-field error map.
 * @returns The rendered start-time and end-time picker rows.
 */
const EventRangePickers = ({
  startDate,
  startTime,
  endDate,
  endTime,
  error,
  onChangePicker,
}: EventRangePickersProps) => {
  const {
    jumpStartRef,
    jumpEndRef,
    pickStartRef,
    pickEndRef,
    onChange,
    onClickJumpTo,
    onClickPickOn,
  } = useEventRangePickers({ onChangePicker });

  return (
    <>
      <div className="time-input mb-3">
        <span className="time-input-title">Start time</span>
        <div className="time-input-row">
          <DatePickerInput
            className="time-input-custom__date pr2"
            name="startDate"
            placeholder="MM/DD/YYYY"
            value={startDate}
            onChange={(date, dateString) => onChange('startDate', date, dateString)}
            disabled
            isError={!_.isEmpty(error.startDate)}
            errorMessage={error.startDate}
          />
          <TimePickerInput
            className="time-input-custom__time"
            value={startTime}
            onChange={(time, timeString) => onChange('startTime', time, timeString)}
            disabled
            placeholder="HH:mm:ss"
            format="HH:mm:ss"
            isError={!_.isEmpty(error.startTime)}
            errorMessage={error.startTime}
          />
          <div ref={jumpStartRef}>
            <IconButton
              className="ms-3"
              isFilled
              disabled={!startDate}
              iconComponent={<img src={icTarget} alt="Jump to" />}
              onClick={() => {
                onClickJumpTo(startDate, true);
              }}
            />
          </div>
          <div ref={pickStartRef}>
            <IconButton
              className="ms-3"
              isFilled
              iconComponent={<img src={icPick} alt="Pick on ECG viewer" />}
              onClick={() => {
                onClickPickOn(true);
              }}
            />
          </div>
          <UncontrolledTooltip
            className="custom-tooltip"
            target={jumpStartRef}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Jump to
          </UncontrolledTooltip>
          <UncontrolledTooltip
            className="custom-tooltip"
            target={pickStartRef}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Pick on ECG viewer
          </UncontrolledTooltip>
        </div>
      </div>
      <div className="time-input">
        <span className="time-input-title">End time</span>
        <div className="time-input-row">
          <DatePickerInput
            className="time-input-custom__date pr2"
            name="endDate"
            placeholder="MM/DD/YYYY"
            value={endDate}
            onChange={(date, dateString) => onChange('endDate', date, dateString)}
            disabled
            isError={!_.isEmpty(error.endDate)}
            errorMessage={error.endDate}
          />
          <TimePickerInput
            className="time-input-custom__time"
            value={endTime}
            onChange={(time, timeString) => onChange('endTime', time, timeString)}
            disabled
            placeholder="HH:mm:ss"
            format="HH:mm:ss"
            isError={!_.isEmpty(error.endTime)}
            errorMessage={error.endTime}
          />
          <div ref={jumpEndRef}>
            <IconButton
              className="ms-3"
              isFilled
              disabled={!endDate}
              iconComponent={<img src={icTarget} alt="Jump to" />}
              onClick={() => {
                onClickJumpTo(endDate, false);
              }}
            />
          </div>
          <div ref={pickEndRef}>
            <IconButton
              className="ms-3"
              isFilled
              iconComponent={<img src={icPick} alt="Pick on ECG viewer" />}
              onClick={() => {
                onClickPickOn(false);
              }}
            />
          </div>
          <UncontrolledTooltip
            className="custom-tooltip"
            target={jumpEndRef}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Jump to
          </UncontrolledTooltip>
          <UncontrolledTooltip
            className="custom-tooltip"
            target={pickEndRef}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Pick on ECG viewer
          </UncontrolledTooltip>
        </div>
      </div>
    </>
  );
};

export default EventRangePickers;
